import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Chip, EmptyState, Input, Skeleton } from '../components/ui'
import { PageHeader } from '../components/PageHeader'
import { useCommands, useDoors } from '../api/hooks'
import { api, ApiError } from '../api/client'
import type { CommandLog, Door, Page } from '../api/types'
import { useAuthStore } from '../stores/auth'
import { relativeTime, timeOfDay } from '../lib/format'
import { DOOR_KIND_LABELS } from './Doors'

function commandStatus(log: CommandLog): CommandLog['status'] {
  return log.status
}

function CommandStatusChip({ status }: { status: string }) {
  const map: Record<string, { tone: 'ok' | 'warn' | 'danger' | 'muted' | 'info'; label: string }> = {
    pending: { tone: 'info', label: 'Pending' },
    running: { tone: 'warn', label: 'Running' },
    succeeded: { tone: 'ok', label: 'Succeeded' },
    partial: { tone: 'warn', label: 'Partial' },
    failed: { tone: 'danger', label: 'Failed' },
  }
  const { tone, label } = map[status] ?? { tone: 'muted' as const, label: status }
  return <Chip tone={tone} label={label} />
}

/** /commands is keyset-paginated ({items, next_cursor}). */
function commandList(data: Page<CommandLog> | undefined): CommandLog[] {
  return data?.items ?? []
}

export default function UnlockPage() {
  const customer = useAuthStore((s) => s.customer)
  const scope = customer?.unique_id ?? 'none'
  const queryClient = useQueryClient()

  const doorsQuery = useDoors()
  const commands = useCommands()

  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string | null>(null)
  const [armed, setArmed] = useState(false)
  const [result, setResult] = useState<CommandLog | null>(null)

  const doors = doorsQuery.data ?? []
  const doorNames = new Map(doors.map((d) => [d.unique_id, d.name]))

  const filtered = doors.filter((d) => {
    const path = d.breadcrumb.join(' / ')
    return (
      query.trim() === '' ||
      d.name.toLowerCase().includes(query.trim().toLowerCase()) ||
      path.toLowerCase().includes(query.trim().toLowerCase())
    )
  })
  const groups = new Map<string, Door[]>()
  for (const d of filtered) {
    const key = d.breadcrumb.join(' / ') || 'Doors'
    groups.set(key, [...(groups.get(key) ?? []), d])
  }

  const selectedDoor = doors.find((d) => d.unique_id === selected) ?? null

  const openDoor = useMutation({
    mutationFn: api.openDoor,
    onSuccess: (log) => {
      setResult(log)
      setArmed(false)
      queryClient.invalidateQueries({ queryKey: [scope, 'commands'] })
    },
    onError: () => setArmed(false),
  })

  // The confirm state disarms itself after a few seconds.
  useEffect(() => {
    if (!armed) return
    const t = setTimeout(() => setArmed(false), 5000)
    return () => clearTimeout(t)
  }, [armed])

  const recentUnlocks = commandList(commands.data).filter((c) => c.kind === 'open_door').slice(0, 10)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader section="Remote unlock" title="Open door">
        <p className="mt-1 text-xs text-ostrel-ink-muted">
          A 5-second unlock pulse is sent through the vendor connector and written to the audit trail.
        </p>
      </PageHeader>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card title="Choose door">
          <Input
            label="Search"
            placeholder="Door name or location…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="mt-3 flex max-h-[28rem] flex-col gap-1 overflow-y-auto">
            {doorsQuery.isPending ? (
              <>
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
              </>
            ) : doorsQuery.isError ? (
              <EmptyState title="Could not load doors" hint={doorsQuery.error.message} />
            ) : filtered.length === 0 ? (
              <EmptyState title="No doors found" hint="Doors appear here once they are created or synced in." />
            ) : (
              [...groups.entries()].map(([path, list]) => (
                <div key={path}>
                  <p className="px-2 pt-3 pb-1 text-[9px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                    {path}
                  </p>
                  {list.map((d) => (
                    <button
                      key={d.unique_id}
                      onClick={() => {
                        setSelected(d.unique_id)
                        setArmed(false)
                        setResult(null)
                        openDoor.reset()
                      }}
                      className={`flex w-full items-center gap-2.5 rounded-sm border px-3 py-2.5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-ostrel-gold ${
                        selected === d.unique_id
                          ? 'border-ostrel-gold bg-ostrel-gold/10'
                          : 'border-ostrel-line bg-ostrel-card hover:bg-ostrel-sand'
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-ostrel-ink">{d.name}</span>
                        <span className="tnum block truncate font-mono text-[10px] text-ostrel-ink-muted">
                          {d.unique_id}
                        </span>
                      </span>
                      {/* Mailboxes/trash are unlockable openings too — the
                          kind just rides along as a chip. */}
                      {d.kind && d.kind !== 'door' && <Chip tone="info" label={DOOR_KIND_LABELS[d.kind]} />}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </Card>

        <div className="flex flex-col gap-4">
          <Card title="Confirm unlock">
            {!selectedDoor ? (
              <EmptyState title="No door selected" hint="Pick a door from the list to arm the unlock pulse." />
            ) : (
              <div className="flex flex-col gap-4">
                <div>
                  <p className="font-display text-xl text-ostrel-ink">{selectedDoor.name}</p>
                  <p className="mt-0.5 text-xs text-ostrel-ink-muted">
                    {[...selectedDoor.breadcrumb, selectedDoor.name].join(' / ')}
                  </p>
                </div>
                <Button
                  variant={armed ? 'primary' : 'navy'}
                  className="w-full py-3"
                  disabled={openDoor.isPending}
                  onClick={() => {
                    if (!armed) {
                      setArmed(true)
                      return
                    }
                    openDoor.mutate(selectedDoor.unique_id)
                  }}
                >
                  {openDoor.isPending ? 'Sending pulse…' : armed ? 'Confirm unlock' : 'Open door'}
                </Button>
                {armed && !openDoor.isPending && (
                  <p className="text-center text-[11px] text-ostrel-warn">
                    Press again within 5 seconds to send the unlock pulse.
                  </p>
                )}
                {openDoor.isError && (
                  <p className="text-xs text-ostrel-danger">
                    {openDoor.error instanceof ApiError ? openDoor.error.detail : openDoor.error.message}
                  </p>
                )}
              </div>
            )}

            {result && (
              <div className="mt-4 rounded-sm border border-ostrel-line bg-ostrel-sand/60 p-3">
                <div className="flex items-center gap-2">
                  <CommandStatusChip status={commandStatus(result)} />
                  <code className="tnum font-mono text-[11px] text-ostrel-ink-muted">{result.unique_id}</code>
                  <span className="tnum ml-auto font-mono text-[11px] text-ostrel-ink-muted">
                    {timeOfDay(result.created_at)}
                  </span>
                </div>
                {result.results.length > 0 && (
                  <ul className="mt-2.5 flex flex-col gap-1.5">
                    {result.results.map((r, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs">
                        <span
                          className={`inline-block h-1.5 w-1.5 rounded-full ${
                            r.ok ? 'bg-ostrel-ok' : 'bg-ostrel-danger'
                          }`}
                        />
                        <span className="tnum font-mono text-[11px] text-ostrel-ink">{r.system ?? 'system'}</span>
                        {r.detail && <span className="truncate text-ostrel-ink-muted">{r.detail}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {(commandStatus(result) === 'pending' || commandStatus(result) === 'running') && (
                  <p className="mt-2 text-[11px] text-ostrel-ink-muted">
                    The command was accepted and is being executed by the connector.
                  </p>
                )}
              </div>
            )}
          </Card>

          <Card title="Recent unlocks">
            {commands.isPending ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-8" />
                <Skeleton className="h-8" />
                <Skeleton className="h-8" />
              </div>
            ) : commands.isError ? (
              <EmptyState title="Could not load commands" hint={commands.error.message} />
            ) : recentUnlocks.length === 0 ? (
              <EmptyState title="No unlocks yet" hint="Remote unlocks are logged here with their outcome." />
            ) : (
              <ul className="flex flex-col divide-y divide-ostrel-line">
                {recentUnlocks.map((c) => (
                  <li key={c.unique_id} className="flex items-center gap-3 py-2">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ostrel-ink">
                        {c.target_unique_id ? (doorNames.get(c.target_unique_id) ?? c.target_unique_id) : c.unique_id}
                      </span>
                      <span className="tnum block truncate font-mono text-[10px] text-ostrel-ink-muted">
                        {c.actor_unique_id ?? c.unique_id}
                      </span>
                    </span>
                    <CommandStatusChip status={commandStatus(c)} />
                    <span className="tnum shrink-0 text-[11px] text-ostrel-ink-muted">
                      {relativeTime(c.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
